import { CANCELABLE_DISPLAY_STATUSES } from '@constants'
import { type WpOrderStatus, type WpOrderType, ORIGINAL_ORDER_STATUS, PAYMENT_TRANSACTION_STATUS, WP_ORDER_STATUS, WP_ORDER_TYPE } from '@hooks-api/types/dataModel/eCommerceOrders'
import { type OrderListDTO } from '../utils/transformOrderDataDTO'

interface GetOrderCardUiRulesParams {
  order: OrderListDTO
  status: WpOrderStatus
  type: WpOrderType
}

interface OrderCardUiRules {
  /** 是否為 fastpass 交易訂單（transactionId 存在） */
  isTransaction: boolean
  /** 是否為寄倉或訂閱訂單 */
  isWarehouseOrderOrSubscriptionOrder: boolean
  /** 取消訂單按鈕（pending_payment / pending_shipment / preparing_shipment） */
  shouldShowCancelOrder: boolean
  /** 取消自動購買按鈕（自動續訂 + 非首單 + 未取消 + pending/uncreated） */
  shouldShowCancelSubscriptionButton: boolean
  /** 扣款卡號 Chip（取消訂閱按鈕可見 + 有信用卡資訊） */
  shouldShowPaymentCardChip: boolean
  /** 預購標籤（on-hold / on-hold-1 / on-hold-2） */
  shouldShowPreOrderBadge: boolean
  /** 定期扣款日文字（寄倉/訂閱 + 預產 + 未顯示付款失敗紅字） */
  shouldShowRecurringPaymentDate: boolean
  /** 訂閱訂單預估配送日文字 */
  shouldShowSubscriptionEstimatedDate: boolean
  /** 退款狀態 Badge */
  shouldShowRefundStatus: boolean
  /** 再買一次按鈕（一般/交易訂單 + 已完成或已取消） */
  shouldShowRepeatButton: boolean
  /** 配送設定折疊區塊（訂閱訂單 + 非首單 pending） */
  shouldShowShippingSetting: boolean
  /** [Tablet] 寄倉處理中配送日期+數量（需 estimatedDeliveryDate） */
  shouldShowTabletDeliveryInfo: boolean
  /** [Tablet] 寄倉/訂閱付款失敗文字（母訂單 + 交易待付款） */
  shouldShowTabletPaymentFailed: boolean
  /** [Mobile] 寄倉處理中配送日期+數量（不需 estimatedDeliveryDate） */
  shouldShowMobileDeliveryInfo: boolean
  /** [Mobile] 寄倉/訂閱付款失敗文字（母訂單 + 交易待付款） */
  shouldShowMobilePaymentFailed: boolean
  /** 立即付款按鈕（依 BE `is_payable`：pending 即可付，自動續訂單另需進入配送日前 5 天窗口，避免過早扣款） */
  shouldShowPayButton: boolean
}

export function getOrderCardUiRules({ order, status, type }: GetOrderCardUiRulesParams): OrderCardUiRules {
  const {
    originalStatus,
    transactionId,
    isSubscriptionOrder,
    isAutoRenew,
    isPrecreateStatus,
    fortuneOrderId,
    isCancelledServiceOrder,
    orderStatus,
    paymentTokenId,
    creditCardLast4,
    refundStatus,
    displayStatus,
    estimatedDeliveryDate,
    displayNextDeliveryQuantity,
    displayNextDeliveryDate,
    paymentFailedCount,
    paymentTransactionStatus,
    isChildOrder,
  } = order

  // ── 基礎判斷 ──
  const isPendingOrder = status === WP_ORDER_STATUS.PENDING
  const isProcessingOrder = status === WP_ORDER_STATUS.PROCESSING || originalStatus === ORIGINAL_ORDER_STATUS.COMPLETED
  const isWarehouseOrder = type === WP_ORDER_TYPE.SERVICE_ORDER
  const isInSubscriptionOrderList = type === WP_ORDER_TYPE.SUBSCRIBE
  const isWarehouseOrderOrSubscriptionOrder = isWarehouseOrder || isInSubscriptionOrderList
  const isWarehouseProcessingOrder = isWarehouseOrder && isProcessingOrder
  const isSubscriptionProcessingOrder = isInSubscriptionOrderList && isProcessingOrder
  const isPaymentFailed = (paymentFailedCount ?? 0) > 0
  const isAwaitingPayment = paymentTransactionStatus === PAYMENT_TRANSACTION_STATUS.PENDING
  const isPendingParentOrder = (isPendingOrder || orderStatus === WP_ORDER_STATUS.PENDING) && !isPrecreateStatus
  const isUncreatedOrderStatus = orderStatus === WP_ORDER_STATUS.UNCREATED
  const isTransaction = transactionId !== null
  const isEditableSubscriptionStage = orderStatus === WP_ORDER_STATUS.PENDING || isUncreatedOrderStatus || !!isPrecreateStatus

  // ── UI 顯示 Flag ──
  const shouldShowCancelOrder = isTransaction && !!displayStatus && CANCELABLE_DISPLAY_STATUSES.has(displayStatus)
  const shouldShowCancelSubscriptionButton = !!isAutoRenew && !isPendingParentOrder && !isCancelledServiceOrder && !!fortuneOrderId && isEditableSubscriptionStage
  const shouldShowPaymentCardChip = shouldShowCancelSubscriptionButton && !!paymentTokenId && !!creditCardLast4
  const shouldShowPreOrderBadge = originalStatus === ORIGINAL_ORDER_STATUS.ON_HOLD || originalStatus === ORIGINAL_ORDER_STATUS.ON_HOLD_1 || originalStatus === ORIGINAL_ORDER_STATUS.ON_HOLD_2
  /** payment_failed_count 會由子訂單同步到母訂單、且重新扣款成功後不歸零，故另外限定母訂單 + 該筆交易仍待付款 */
  const shouldShowPaymentFailed = isWarehouseOrderOrSubscriptionOrder && isPaymentFailed && !isChildOrder && isAwaitingPayment
  /** 與紅字互斥：紅字不出現時（含次數為舊帳的情況）改回顯示定期扣款日，避免狀態列空白 */
  const shouldShowRecurringPaymentDate = isWarehouseOrderOrSubscriptionOrder && isEditableSubscriptionStage && !!isPrecreateStatus && !shouldShowPaymentFailed
  const shouldShowSubscriptionEstimatedDate = isSubscriptionProcessingOrder && !!estimatedDeliveryDate
  const shouldShowRefundStatus = !!refundStatus
  const shouldShowRepeatButton = (type === WP_ORDER_TYPE.NORMAL || type === WP_ORDER_TYPE.PAYMENT_TRANSACTION || type === WP_ORDER_TYPE.CAMPAIGN) && (!!isCancelledServiceOrder || orderStatus === WP_ORDER_STATUS.CANCELLED || orderStatus === WP_ORDER_STATUS.COMPLETED || orderStatus === WP_ORDER_STATUS.REFUNDED)
  const shouldShowShippingSetting = !isPendingParentOrder && !!isSubscriptionOrder

  // ── Tablet 專用 Flag ──
  const shouldShowTabletDeliveryInfo = !!estimatedDeliveryDate && isWarehouseProcessingOrder && !!displayNextDeliveryQuantity && !!displayNextDeliveryDate
  const shouldShowTabletPaymentFailed = shouldShowPaymentFailed

  // ── Mobile 專用 Flag ──
  const shouldShowMobileDeliveryInfo = isWarehouseProcessingOrder && !!displayNextDeliveryQuantity && !!displayNextDeliveryDate
  const shouldShowMobilePaymentFailed = shouldShowPaymentFailed
  const shouldShowPayButton = order.isPayable ?? false

  return {
    isTransaction,
    isWarehouseOrderOrSubscriptionOrder,
    shouldShowCancelOrder,
    shouldShowCancelSubscriptionButton,
    shouldShowPaymentCardChip,
    shouldShowPreOrderBadge,
    shouldShowRecurringPaymentDate,
    shouldShowSubscriptionEstimatedDate,
    shouldShowRefundStatus,
    shouldShowRepeatButton,
    shouldShowShippingSetting,
    shouldShowTabletDeliveryInfo,
    shouldShowTabletPaymentFailed,
    shouldShowMobileDeliveryInfo,
    shouldShowMobilePaymentFailed,
    shouldShowPayButton,
  }
}
